// File: webelieve-frontend/src/features/jobs/ParaLexSummaryPanel.tsx
// ParaLex MCP Agent Integration for EdgeLex System Monitor
// 🚀 ENHANCED: Now using useMcpJobAnalysis hook for global state management

import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { JobDetail } from './jobTypes';
import { getMcpHealthStatus } from './mcpApi';
import { useMcpJobAnalysis } from './useMcpJobAnalysis';

// ParaLex MCP API interfaces (matching backend types)
interface ParaLexAnalysisResponse {
  analysis_id: string;
  trace_id: string;
  summary: string;
  failure_point?: string;
  root_cause?: string;
  recommendations: string[];
  next_steps?: string[];
  severity: 'low' | 'medium' | 'high' | 'critical';
  categories: string[];
  retry_safe: boolean;
  estimated_fix_time?: string;
  related_failures?: string[];
  notifications?: {
    email?: boolean;
    slack?: boolean;
    retry?: boolean;
  };
  metadata: {
    analysis_time_ms: number;
    model_used: string;
    confidence_score: number;
    language_detected?: string;
  };
}

interface ParaLexError {
  error: string;
  trace_id?: string;
  analysis_time_ms?: number;
  suggestion?: string;
}

interface ParaLexSummaryPanelProps {
  job: JobDetail | null;
  isVisible?: boolean;
  onAnalysisComplete?: (analysis: ParaLexAnalysisResponse) => void;
  onAnalysisError?: (error: ParaLexError) => void;
}

export default function ParaLexSummaryPanel({
  job,
  isVisible = true,
  onAnalysisComplete,
  onAnalysisError
}: ParaLexSummaryPanelProps) {
  const { keycloak } = useKeycloak();
  
  // Use MCP Job Analysis hook for global state management
  const {
    analyzeFailedJob,
    isAnalyzing,
    getAnalysis,
    error: hookError,
    clearError
  } = useMcpJobAnalysis();
  
  // Component-specific state (preserving existing behavior)
  const [autoAnalyzeEnabled, setAutoAnalyzeEnabled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [serviceHealth, setServiceHealth] = useState<'unknown' | 'healthy' | 'unhealthy'>('unknown');

  // Get analysis data from hook
  const analysis = job ? getAnalysis(job.id) : null;
  const loading = job ? isAnalyzing : false;
  const error: ParaLexError | null = hookError ? { error: hookError, trace_id: job?.id } : null;

  // Check ParaLex service health on mount
  useEffect(() => {
    checkServiceHealth();
  }, []);

  // Auto-analyze when job changes (if enabled and job is failed)
  useEffect(() => {
    if (autoAnalyzeEnabled && job && job.status === 'failed' && !loading) {
      console.log('[ParaLexSummaryPanel] Auto-analyzing failed job:', job.id);
      handleAnalyze();
    }
  }, [job, autoAnalyzeEnabled]);

  /**
   * Check ParaLex service health
   */
  const checkServiceHealth = async () => {
    if (!keycloak?.token) return;

    try {
      const health = await getMcpHealthStatus(keycloak.token);
      setServiceHealth(health.status === 'healthy' ? 'healthy' : 'unhealthy');
    } catch (error) {
      console.error('[ParaLexSummaryPanel] Health check failed:', error);
      setServiceHealth('unhealthy');
    }
  };

  /**
   * Trigger ParaLex analysis for the current job using hook
   */
  const handleAnalyze = async () => {
    if (!job || !keycloak?.token) {
      console.warn('[ParaLexSummaryPanel] Cannot analyze - missing job or token');
      return;
    }

    // Clear any previous errors
    clearError();

    try {
      console.log(`[ParaLexSummaryPanel] Starting MCP analysis for job ${job.id}`);

      // Use hook's analyzeFailedJob function
      const analysisResult = await analyzeFailedJob(job.id, {
        realmId: job.data?._meta?.realmId || job.data?.realmId || 'default',
        analysisType: 'comprehensive',
        mcpStrategy: 'auto'
      });
      
      console.log(`[ParaLexSummaryPanel] MCP analysis completed`);
      
      setExpanded(true); // Auto-expand on successful analysis
      
      // Notify parent component - convert hook result to expected format
      if (onAnalysisComplete && analysisResult) {
        const convertedResult: ParaLexAnalysisResponse = {
          analysis_id: analysisResult.jobId,
          trace_id: job.data?._meta?.traceId || job.id,
          summary: analysisResult.summary || '',
          failure_point: analysisResult.analysis?.rootCause,
          root_cause: analysisResult.analysis?.rootCause,
          recommendations: analysisResult.analysis?.recommendations || [],
          next_steps: analysisResult.analysis?.quickFixes || [],
          severity: analysisResult.analysis?.severity || 'medium',
          categories: [analysisResult.analysis?.failureCategory || 'unknown'],
          retry_safe: analysisResult.analysis?.quickFixes?.length > 0,
          metadata: {
            analysis_time_ms: analysisResult.orchestration?.executionTime || 0,
            model_used: 'MCP Multi-AI',
            confidence_score: analysisResult.confidence || 0,
          }
        };
        onAnalysisComplete(convertedResult);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('[ParaLexSummaryPanel] Analysis failed:', errorMessage);
      
      // Notify parent component
      if (onAnalysisError) {
        const errorData: ParaLexError = {
          error: errorMessage,
          trace_id: job.data?._meta?.traceId || job.id
        };
        onAnalysisError(errorData);
      }
    }
  };

  /**
   * Get severity badge styling (adapted for JobAnalysisResult)
   */
  const getSeverityBadge = (severity: string) => {
    const baseClass = "px-2 py-1 rounded-full text-xs font-medium";
    switch (severity.toLowerCase()) {
      case 'critical':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      case 'high':
        return `${baseClass} bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200`;
      case 'medium':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'low':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }
  };

  /**
   * Check if analysis indicates retry is safe (adapted for JobAnalysisResult)
   */
  const isRetrySafe = () => {
    return analysis?.analysis?.quickFixes && analysis.analysis.quickFixes.length > 0;
  };

  /**
   * Get service health indicator
   */
  const getHealthIndicator = () => {
    switch (serviceHealth) {
      case 'healthy':
        return <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="ParaLex is healthy" />;
      case 'unhealthy':
        return <div className="w-2 h-2 bg-red-500 rounded-full" title="ParaLex is unhealthy" />;
      default:
        return <div className="w-2 h-2 bg-gray-400 rounded-full" title="ParaLex status unknown" />;
    }
  };

  // Don't render if not visible or no job
  if (!isVisible || !job) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-850 rounded-lg border border-gray-200 dark:border-gray-700 shadow">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">🤖</span>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              ParaLex System Agent
            </h3>
            {getHealthIndicator()}
          </div>
          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 rounded text-xs font-medium">
            Mistral-7B Local
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Auto-analyze toggle */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoAnalyzeEnabled}
              onChange={(e) => setAutoAnalyzeEnabled(e.target.checked)}
              className="rounded"
              disabled={serviceHealth !== 'healthy'}
            />
            <span className="text-gray-600 dark:text-gray-300">Auto-analyze failures</span>
          </label>
          
          {/* Manual analyze button */}
          <button
            onClick={handleAnalyze}
            disabled={loading || serviceHealth !== 'healthy'}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              loading || serviceHealth !== 'healthy'
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Analyzing...</span>
              </div>
            ) : (
              '🤖 MCP Analyze'
            )}
          </button>
          
          {/* Expand/collapse button */}
          {(analysis || error) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {expanded ? '🔼' : '🔽'}
            </button>
          )}
        </div>
      </div>

      {/* Service Health Warning */}
      {serviceHealth === 'unhealthy' && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2">
            <span className="text-red-600 dark:text-red-400">⚠️</span>
            <p className="text-sm text-red-800 dark:text-red-200">
              MCP ParaLex service is unavailable. Check that MCP servers and AI providers are running.
            </p>
            <button
              onClick={checkServiceHealth}
              className="ml-auto text-red-600 hover:text-red-800 text-sm underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="p-6 text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600 dark:text-gray-300">
              MCP ParaLex is analyzing trace {job.data?._meta?.traceId || job.id} with multi-AI orchestration...
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            This may take 10-30 seconds for comprehensive MCP analysis
          </p>
        </div>
      )}

      {/* Error State */}
      {error && expanded && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
          <div className="flex items-start gap-3">
            <span className="text-red-600 dark:text-red-400 text-lg">❌</span>
            <div className="flex-1">
              <h4 className="font-semibold text-red-800 dark:text-red-200">Analysis Failed</h4>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                {error.error}
              </p>
              {error.suggestion && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2 italic">
                  💡 {error.suggestion}
                </p>
              )}
            </div>
            <button
              onClick={clearError}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && expanded && (
        <div className="p-4 space-y-4">
          {/* Summary */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-lg">📋</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Analysis Summary</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  {analysis.summary}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={getSeverityBadge(analysis.analysis?.severity || 'medium')}>
                  {(analysis.analysis?.severity || 'MEDIUM').toUpperCase()}
                </span>
                {isRetrySafe() && (
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded text-xs font-medium">
                    RETRY SAFE
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Failure Point & Root Cause */}
          {analysis.analysis?.rootCause && (
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <span className="text-red-600 dark:text-red-400">🔍</span>
                  <div>
                    <h4 className="font-semibold text-red-800 dark:text-red-200 text-sm">
                      Root Cause
                    </h4>
                    <p className="text-red-700 dark:text-red-300 text-sm mt-1">
                      {analysis.analysis.rootCause}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {analysis.analysis?.recommendations && analysis.analysis.recommendations.length > 0 && (
            <div className="bg-lex-accent-soft rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-lex-accent">💡</span>
                <div className="flex-1">
                  <h4 className="font-semibold text-lex-accent text-sm mb-2">
                    Recommendations
                  </h4>
                  <ul className="space-y-1">
                    {analysis.analysis.recommendations.map((rec, index) => (
                      <li key={index} className="text-lex-accent text-sm flex items-start gap-2">
                        <span className="text-lex-accent mt-1">•</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Quick Fixes (Next Steps) */}
          {analysis.analysis?.quickFixes && analysis.analysis.quickFixes.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-green-600 dark:text-green-400">🚀</span>
                <div className="flex-1">
                  <h4 className="font-semibold text-green-800 dark:text-green-200 text-sm mb-2">
                    Quick Fixes
                  </h4>
                  <ul className="space-y-1">
                    {analysis.analysis.quickFixes.map((step, index) => (
                      <li key={index} className="text-green-700 dark:text-green-300 text-sm flex items-start gap-2">
                        <span className="text-green-500 mt-1">{index + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Analysis Metadata */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-4">
                <span>⏱️ {analysis.orchestration?.executionTime || 0}ms</span>
                <span>🤖 {analysis.orchestration?.aiProvidersUsed?.join(', ') || 'MCP Multi-AI'}</span>
                <span>🎯 {Math.round((analysis.confidence || 0) * 100)}% confidence</span>
                <span>💰 ${(analysis.orchestration?.cost || 0).toFixed(4)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">
                  {analysis.analysis?.failureCategory || 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed State Summary */}
      {analysis && !expanded && (
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                ✅ Analysis completed
              </span>
              <span className={getSeverityBadge(analysis.analysis?.severity || 'medium')}>
                {(analysis.analysis?.severity || 'MEDIUM').toUpperCase()}
              </span>
              {isRetrySafe() && (
                <span className="text-xs text-green-600 dark:text-green-400">
                  Safe to retry
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {analysis.orchestration?.executionTime || 0}ms • {Math.round((analysis.confidence || 0) * 100)}% confidence
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 